import { Search } from 'lucide-react'
import { Select } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import type { DateRangeValue } from '@/components/ui/date-range-picker'
import type { ChangeEvent } from 'react'
import { FilterFormWrapper } from '@/components/FilterFormWrapper'
import { fetchUnits } from '@/api/units.api'

export interface RobotFilterValues {
  keyword: string
  unit: string | undefined
  unitName?: string
  status: number | undefined
  deleted: number | undefined
  time: DateRangeValue | undefined
}

interface RobotFilterPanelProps {
  values: RobotFilterValues
  onChange: (values: RobotFilterValues) => void
  onSearch: () => void
  showUnitFilter?: boolean
}

export function RobotFilterPanel({
  values,
  onChange,
  onSearch,
  showUnitFilter = true,
}: RobotFilterPanelProps) {
  const updateFilter = <TKey extends keyof RobotFilterValues>(
    key: TKey,
    value: RobotFilterValues[TKey],
  ) => {
    onChange({ ...values, [key]: value })
  }

  const getSelectedState = () => {
    if (values.deleted === 1) return 'deleted'
    if (values.status === 1) return 'active'
    if (values.status === 0) return 'inactive'
    return undefined
  }

  const handleStateChange = (value: string | undefined) => {
    if (value === 'active') {
      onChange({ ...values, status: 1, deleted: 0 })
    } else if (value === 'inactive') {
      onChange({ ...values, status: 0, deleted: 0 })
    } else if (value === 'deleted') {
      onChange({ ...values, status: undefined, deleted: 1 })
    } else {
      onChange({ ...values, status: undefined, deleted: undefined })
    }
  }

  return (
    <FilterFormWrapper
      onSearch={onSearch}
      className="rounded-xl border border-gray-200 bg-white px-6 py-5"
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex min-w-[200px] flex-1 flex-col gap-1">
          <span className="text-xs">Tìm kiếm tên robot</span>
          <Input
            placeholder="Nhập từ khóa"
            value={values.keyword}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              updateFilter('keyword', e.target.value)
            }
          />
        </div>
        {showUnitFilter && (
          <div className="flex min-w-[200px] flex-1 flex-col gap-1">
            <span className="text-xs">Đơn vị</span>
            <Select
              cacheKey="robot-filter-unit-select"
              initialPage={0}
              lazy
              placeholder="Tất cả"
              allowClear
              value={values.unit ?? undefined}
              className="w-full"
              enableRemoteSearch
              allLabel="Tất cả"
              initialOptions={
                values.unit && values.unitName
                  ? [{ value: values.unit, label: values.unitName }]
                  : []
              }
              loadOptions={async ({ page, signal, searchText }) => {
                const res = await fetchUnits(
                  page,
                  10,
                  { keySearch: searchText?.trim() || undefined },
                  signal,
                )

                const arr = Array.isArray(res.items) ? res.items : []
                const pageSize = 10
                const hasNextPage = arr.length >= pageSize

                return {
                  options: arr.map((u: any) => ({
                    value: u.id,
                    label: u.name,
                  })),
                  hasNextPage,
                  nextPage: hasNextPage ? page + 1 : undefined,
                }
              }}
              onLazyValueChange={(value, option) => {
                onChange({
                  ...values,
                  unit: value,
                  unitName: option?.label ? String(option.label) : undefined,
                })
              }}
            />
          </div>
        )}

        <div className="flex min-w-[200px] flex-1 flex-col gap-1">
          <span className="text-xs">Hiện trạng</span>
          <Select
            placeholder="Tất cả"
            value={getSelectedState()}
            className="w-full"
            onValueChange={handleStateChange}
            allowClear
            options={[
              { value: 'active', label: 'Hoạt động' },
              { value: 'inactive', label: 'Không hoạt động' },
              { value: 'deleted', label: 'Đã xóa' },
            ]}
          />
        </div>
        <div className="flex min-w-[200px] flex-1 flex-col gap-1">
          <span className="text-xs">Thời gian</span>
          <DateRangePicker
            value={values.time}
            onChange={(value) => updateFilter('time', value)}
            className="w-full"
          />
        </div>
        <Button
          variant="outline"
          size="md"
          className="shrink-0 gap-2 border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10"
          onClick={onSearch}
        >
          <Search size={16} />
          Tìm kiếm
        </Button>
      </div>
    </FilterFormWrapper>
  )
}
